// Tumbling 3D chunks: appliance panels, bolts, toast, rocks. Instanced boxes flown and spun
// entirely in the vertex shader (same ballistic model as the particles), lit with the same
// two hard bands as everything else. One draw for every piece of junk in the air.

import * as THREE from 'three';
import { Ring, hazeUniforms, HAZE_VS } from './gpu';
import type { RGB } from './particles';

const STRIDE = 24;

const VS = /* glsl */ `
${HAZE_VS}
attribute vec4 aA; // p0, t0
attribute vec4 aB; // v, life
attribute vec4 aC; // scale xyz, floor
attribute vec4 aD; // spin axis xyz, spin rate
attribute vec4 aE; // colour rgb, gravity
attribute vec4 aF; // drag, phase, -, -
attribute vec3 color;
uniform float uTime;
varying vec3 vCol;
varying float vLit;
varying float vHaze;
varying vec3 vHazeCol;
varying vec3 vLp;
varying float vSeed;
varying vec3 vP;
varying float vTop;
vec3 rotA( vec3 v, vec3 k, float a ) {
  float c = cos( a ), s = sin( a );
  return v * c + cross( k, v ) * s + k * dot( k, v ) * ( 1.0 - c );
}
void main() {
  float age = uTime - aA.w;
  float life = aB.w;
  if ( age < 0.0 || age > life ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  float lt = age / life;
  vec3 g = vec3( 0.0, -aE.w, 0.0 );
  float k = max( aF.x, 0.001 );
  float e = exp( -k * age );
  vec3 pos = aA.xyz + ( aB.xyz - g / k ) * ( 1.0 - e ) / k + ( g / k ) * age;
  float half_ = max( aC.x, max( aC.y, aC.z ) ) * 0.5;
  float landed = step( pos.y, aC.w + half_ * 0.6 );
  pos.y = max( pos.y, aC.w + half_ * 0.6 );
  // spin settles (and stops once it hits the ground) so pieces come to rest
  float ang = aD.w * ( 1.0 - exp( -1.6 * age ) ) / 1.6 + aF.y;
  float shrink = 1.0 - smoothstep( 0.78, 1.0, lt );
  vec3 lp = position * aC.xyz * shrink;
  vec3 ax = normalize( aD.xyz + vec3( 1e-4 ) );
  vec3 wp = pos + rotA( lp, ax, ang );
  vec3 n = rotA( normal, ax, ang );
  if ( length( pos - cameraPosition ) < 1.6 ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  gl_Position = projectionMatrix * viewMatrix * vec4( wp, 1.0 );
  vec3 sun = normalize( uSunDir );
  vLit = dot( n, sun ) + n.y * 0.35;
  vCol = aE.rgb * color;
  vLp = position * aC.xyz * 9.0;
  vP = position;
  vTop = step( 0.9, color.r );
  vSeed = aF.y;
  vHaze = hazeAt( pos, vHazeCol ) * 0.5;
}
`;

const FS = /* glsl */ `
varying vec3 vCol;
varying float vLit;
varying float vHaze;
varying vec3 vHazeCol;
varying vec3 vLp;
varying float vSeed;
varying vec3 vP;
varying float vTop;
float h3( vec3 p ) { return fract( sin( dot( p, vec3( 127.1, 311.7, 74.7 ) ) ) * 43758.5453 ); }
float n3( vec3 p ) {
  vec3 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( mix( h3( i ), h3( i + vec3( 1, 0, 0 ) ), f.x ), mix( h3( i + vec3( 0, 1, 0 ) ), h3( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
              mix( mix( h3( i + vec3( 0, 0, 1 ) ), h3( i + vec3( 1, 0, 1 ) ), f.x ), mix( h3( i + vec3( 0, 1, 1 ) ), h3( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}
void main() {
  // grunge breakup: soot and grime blotches, chipped paint showing bare metal
  vec3 q = vLp + vSeed * 7.0;
  float g = n3( q ) * 0.65 + n3( q * 2.7 ) * 0.35;
  vec3 base = vCol * mix( 1.0, 0.42, smoothstep( 0.55, 0.72, g ) );
  base = mix( base, vec3( 0.32, 0.3, 0.27 ), step( 0.86, n3( q * 4.1 + 3.0 ) ) * 0.8 );
  // two hard bands; the shadow band leans plum/teal, never grey
  vec3 c = vLit > 0.08 ? base : base * vec3( 0.5, 0.52, 0.6 ) + vec3( 0.0, 0.012, 0.012 );
  // printed ink edge: a dark keyline round each panel face (the side faces are already ink-dark)
  float ed = max( abs( vP.x ), abs( vP.z ) );
  float wob = n3( vLp * 1.7 ) * 0.05;
  c = mix( c, vec3( 0.025, 0.022, 0.03 ), vTop * smoothstep( 0.37 - wob, 0.41 - wob, ed ) );
  gl_FragColor = vec4( mix( c, vHazeCol, vHaze ), 1.0 );
}
`;

export class Debris {
  readonly mesh: THREE.Mesh;
  readonly ring: Ring;
  readonly uniforms = { uTime: { value: 0 } };
  time = 0;
  constructor(cap: number) {
    this.ring = new Ring(cap, STRIDE);
    const r = this.ring;
    // a chunky shard: a box with chipped, jittered corners and flat facets, so pieces read as
    // broken casing rather than flat cards
    const box = new THREE.BoxGeometry(1, 1, 1, 2, 1, 2).toNonIndexed();
    {
      const p = box.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
        const h = Math.sin(x * 37.1 + y * 17.3 + z * 91.7) * 43758.5453; const f = h - Math.floor(h);
        // knock corners off at hard angles (a sheared chip), keep the faces flat
        const corner = Math.abs(x) > 0.4 && Math.abs(z) > 0.4 ? 1 : 0;
        const k = 1 - corner * (0.25 + 0.45 * f);
        const sh = corner * (f - 0.5) * 0.5;
        p.setXYZ(i, x * k + sh * z, y, z * k - sh * x);
      }
      box.computeVertexNormals();
    }
    // edges of each panel read darker (crust on toast, enamel edge on panels)
    const n = box.attributes.normal, cnt = n.count;
    const col = new Float32Array(cnt * 3);
    for (let i = 0; i < cnt; i++) {
      const top = Math.abs(n.getY(i)) > 0.5; // the big faces of a flat panel are ±Y
      const v = top ? 1 : 0.3; // sides ink-dark: two-tone chunk with a printed edge
      col[i * 3] = v; col[i * 3 + 1] = v * 0.97; col[i * 3 + 2] = v * 0.93;
    }
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', box.attributes.position);
    g.setAttribute('normal', box.attributes.normal);
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setAttribute('aA', r.attr(4, 0));
    g.setAttribute('aB', r.attr(4, 4));
    g.setAttribute('aC', r.attr(4, 8));
    g.setAttribute('aD', r.attr(4, 12));
    g.setAttribute('aE', r.attr(4, 16));
    g.setAttribute('aF', r.attr(4, 20));
    g.instanceCount = cap;
    for (let i = 0; i < cap; i++) r.arr[i * STRIDE + 3] = -1e6;
    const m = new THREE.ShaderMaterial({ vertexShader: VS, fragmentShader: FS, uniforms: { ...hazeUniforms(), ...this.uniforms } });
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
  }

  /** sx,sy,sz: size (sy is the thin axis for panels). */
  spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, delay: number,
    sx: number, sy: number, sz: number, floor: number, spin: number, col: RGB, grav = 18, drag = 0.6, k = 1) {
    // a NaN anywhere in an instance becomes NaN pixels, which the HDR bloom smears into blocks
    if (!(x + y + z + vx + vy + vz === x + y + z + vx + vy + vz)) return;
    const a = this.ring.arr, o = this.ring.alloc();
    a[o] = x; a[o + 1] = y; a[o + 2] = z; a[o + 3] = this.time + delay;
    a[o + 4] = vx; a[o + 5] = vy; a[o + 6] = vz; a[o + 7] = life;
    a[o + 8] = sx; a[o + 9] = sy; a[o + 10] = sz; a[o + 11] = floor;
    a[o + 12] = Math.random() - 0.5; a[o + 13] = Math.random() - 0.5; a[o + 14] = Math.random() - 0.5; a[o + 15] = spin;
    a[o + 16] = col[0] * k; a[o + 17] = col[1] * k; a[o + 18] = col[2] * k; a[o + 19] = grav;
    a[o + 20] = drag; a[o + 21] = Math.random() * 6.28; a[o + 22] = 0; a[o + 23] = 0;
  }
  update(time: number) {
    this.time = time;
    this.uniforms.uTime.value = time;
    this.ring.flush();
  }
}
